import { Button } from "@components/buttons/button";
import { Card } from "@components/cards/card";
import { ModalInfo } from "@components/modals/modal-info";
import { ModalPayoutRequest } from "@components/modals/modal-payout-request";
import { useModalContext } from "@hooks/contexts/modal-context";
import { IModalInfoDTO } from "@hooks/dtos/IModalInfoDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { formatName } from "@hooks/functions/formatName";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { config } from "config";
import React, { useEffect, useMemo, useState } from "react";
import {
  FaBullseye,
  FaCircleInfo,
  FaClock,
  FaEye,
  FaEyeSlash,
  FaHandHoldingDollar,
  FaMoneyBillTransfer,
  FaMoneyBillTrendUp,
  FaPeopleGroup,
  FaPercent,
  FaTicket,
  FaTrophy,
  FaWallet,
} from "react-icons/fa6";
import Skeleton from "react-loading-skeleton";
import { Link, useLocation } from "react-router-dom";

interface IModalInfoList {
  [key: string]: {
    header: string;
    title: string;
    text: string;
  };
}

interface IDashboardFinancialSummaryDTO {
  available: number;
  pendingRelease: number;
  total: number;
}

interface IDashboardCurrentOperationDTO {
  activeCampaigns: number;
  soldTickets: number;
  totalTickets: number;
  affiliatesWithSales: number;
  totalAffiliates: number;
  occupancyPercent: number;
}

interface IDashboardWeeklyRevenueDayDTO {
  date: string;
  label: string;
  amount: number;
  amountGross: number;
}

interface IDashboardWeeklyRevenueDTO {
  days: IDashboardWeeklyRevenueDayDTO[];
  totalAmount: number;
  totalAmountGross: number;
  includedOps: number[];
}

interface IDashboardTodayMovementDTO {
  soldTickets: number;
  amountGross: number;
  amount: number;
  generatedCommissions: number;
}

interface IDashboardHighlightCampaignDTO {
  key: string;
  name: string;
  slug: string;
  status: RaffleStatusTypes;
  soldTickets: number;
  totalTickets: number;
  soldPercent: number;
  amountGross: number;
  amount: number;
  affiliatesWithSales: number;
}

interface IDashboardHighlightCampaignsDTO {
  campaigns: IDashboardHighlightCampaignDTO[];
}

interface IDashboardAffiliateRankingDTO {
  affiliates: {
    id: number;
    name: string;
    atSign: string;
    soldTickets: number;
    amountGross: number;
    amount: number;
    commission: number;
    campaignsWithSales: number;
  }[];
}

const masks = new Masks();
const dashboardValuesVisibilityStorageKey = `@${config.appAgent}:dashboard-show-values`;

const mockDashboard = {
  owner: {
    pendingWithdraws: 2,
  },
};

export function Dashboard() {
  const { customer } = ZuAuthStore();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [financialSummary, setFinancialSummary] =
    useState<IDashboardFinancialSummaryDTO>({
      available: 0,
      pendingRelease: 0,
      total: 0,
    });
  const [currentOperation, setCurrentOperation] =
    useState<IDashboardCurrentOperationDTO>({
      activeCampaigns: 0,
      soldTickets: 0,
      totalTickets: 0,
      affiliatesWithSales: 0,
      totalAffiliates: 0,
      occupancyPercent: 0,
    });
  const [weeklyRevenue, setWeeklyRevenue] =
    useState<IDashboardWeeklyRevenueDTO>({
      days: [],
      totalAmount: 0,
      totalAmountGross: 0,
      includedOps: [],
    });
  const [todayMovement, setTodayMovement] =
    useState<IDashboardTodayMovementDTO>({
      soldTickets: 0,
      amountGross: 0,
      amount: 0,
      generatedCommissions: 0,
    });
  const [highlightCampaigns, setHighlightCampaigns] =
    useState<IDashboardHighlightCampaignsDTO>({
      campaigns: [],
    });
  const [affiliateRanking, setAffiliateRanking] =
    useState<IDashboardAffiliateRankingDTO>({
      affiliates: [],
    });
  const [showValues, setShowValues] = useState(() => {
    return localStorage.getItem(dashboardValuesVisibilityStorageKey) === "true";
  });
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    financial: true,
    currentOperation: true,
    weeklyRevenue: true,
    todayMovement: true,
    highlightCampaigns: true,
    affiliateRanking: true,
  });

  const location = useLocation();
  const { changeHash } = useModalContext();

  const [modalInfo, setModalInfo] = useState<IModalInfoDTO>({
    header: ``,
    title: ``,
    text: ``,
  });

  const customerName = useMemo(() => {
    const name =
      customer?.customer?.name ||
      customer?.customer?.userName ||
      "Dono da conta";

    return formatName(name);
  }, [customer]);

  const customerAtSign = useMemo(() => {
    const userName = customer?.customer?.userName;
    if (!userName) {
      return "@sua-conta";
    }

    return userName.startsWith("@") ? userName : `@${userName}`;
  }, [customer]);

  const maxWeeklyRevenue = Math.max(
    ...weeklyRevenue.days.map((item) => item.amount),
    0,
  );

  const modalInfoList: IModalInfoList = {
    balance: {
      header: "Saldo Disponível",
      title: "Resumo Financeiro",
      text: "Valor liberado e disponível para solicitar saque.",
    },
    pendingRelease: {
      header: "Saldo à Liberar",
      title: "Resumo Financeiro",
      text: "Valor que pertence à conta, mas ainda aguarda liberação para saque.",
    },
    total: {
      header: "Saldo Total",
      title: "Resumo Financeiro",
      text: "Soma do saldo disponível com o saldo que ainda está à liberar.",
    },
  };

  function formatMoney(value: number) {
    return showValues ? masks.brlCurrency(Number(value || 0)) : "R$ *****";
  }

  function pluralize(value: number, singular: string, plural: string) {
    return value === 1 ? singular : plural;
  }

  function getModalInfo(param: string) {
    const info = modalInfoList[param];
    if (!info) {
      pushToastify({
        message: `Houve um erro ao carregar as informações.`,
        type: "warning",
        theme: "dark",
      });
      return;
    }

    setModalInfo({
      header: info.header,
      title: info.title,
      text: info.text,
    });

    changeHash("modalInfo");
  }

  async function getDashboardFinancialSummary() {
    try {
      const response = await api.get("/customer/dashboard/financial-summary");

      setFinancialSummary(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar seu saldo.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, financial: false }));
    }
  }

  async function getCurrentOperation() {
    try {
      const response = await api.get("/customer/dashboard/current-operation");

      setCurrentOperation(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar a operação atual.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, currentOperation: false }));
    }
  }

  async function getWeeklyRevenue() {
    try {
      const response = await api.get("/customer/dashboard/weekly-revenue");

      setWeeklyRevenue(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar a receita dos últimos 7 dias.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, weeklyRevenue: false }));
    }
  }

  async function getTodayMovement() {
    try {
      const response = await api.get("/customer/dashboard/today-movement");

      setTodayMovement(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar a movimentação do dia.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, todayMovement: false }));
    }
  }

  async function getHighlightCampaigns() {
    try {
      const response = await api.get("/customer/dashboard/highlight-campaigns");

      setHighlightCampaigns(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar as campanhas em destaque.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, highlightCampaigns: false }));
    }
  }

  async function getAffiliateRanking() {
    try {
      const response = await api.get("/customer/dashboard/affiliate-ranking");

      setAffiliateRanking(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          `Houve um erro ao consultar o ranking de afiliados.`,
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, affiliateRanking: false }));
    }
  }

  useEffect(() => {
    getDashboardFinancialSummary();
    getCurrentOperation();
    getWeeklyRevenue();
    getTodayMovement();
    getHighlightCampaigns();
    getAffiliateRanking();
  }, []);

  useEffect(() => {
    localStorage.setItem(
      dashboardValuesVisibilityStorageKey,
      showValues.toString(),
    );
  }, [showValues]);

  return (
    <>
      <div className="row">
        <div className="col-lg-4">
          <Card className="border-0 shadow-sm h-100">
            <div className="card-body p-4">
              <h3 className="fw-bold mb-2">Olá, {customerName}</h3>
              <div className="text-muted mb-3">{customerAtSign}</div>
              <p className="text-muted mb-4">
                Acompanhe suas rifas, afiliados e movimentações principais.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button className="btn btn-success" to="/raffles/default/list">
                  <FaTicket className="me-2" />
                  Campanhas
                </Button>
                <Button
                  className="btn btn-outline-primary"
                  to="/financial/general"
                >
                  <FaWallet className="me-2" />
                  Financeiro
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-lg-8">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="card-title">Resumo Financeiro</div>
                  <div className="card-category">
                    Saldos disponíveis e valores em liberação.
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowValues((prev) => !prev)}
                  >
                    {showValues ? (
                      <FaEyeSlash className="me-1" />
                    ) : (
                      <FaEye className="me-1" />
                    )}
                    {showValues ? "Ocultar valores" : "Mostrar valores"}
                  </button>
                  <Button
                    className="btn btn-sm btn-success"
                    onClick={() => setIsWithdrawModalOpen(true)}
                  >
                    <FaMoneyBillTransfer className="me-1" />
                    Solicitar Saque
                  </Button>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <FinancialMetric
                    title="Saldo Disponível"
                    value={formatMoney(financialSummary.available)}
                    icon={<FaWallet className="text-primary" />}
                    isLoading={isLoading.financial}
                    onInfo={() => getModalInfo("balance")}
                  />
                </div>
                <div className="col-md-4">
                  <FinancialMetric
                    title="Saldo à Liberar"
                    value={formatMoney(financialSummary.pendingRelease)}
                    icon={<FaMoneyBillTrendUp className="text-warning" />}
                    isLoading={isLoading.financial}
                    onInfo={() => getModalInfo("pendingRelease")}
                  />
                </div>
                <div className="col-md-4">
                  <FinancialMetric
                    title="Saldo Total"
                    value={formatMoney(financialSummary.total)}
                    icon={<FaMoneyBillTransfer className="text-success" />}
                    isLoading={isLoading.financial}
                    onInfo={() => getModalInfo("total")}
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12">
          <Card className="shadow-sm">
            <div className="card-header">
              <div className="card-title">Operação Atual</div>
              <div className="card-category">
                Indicadores acumulados das campanhas ativas.
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <DashboardMiniMetric
                    title="Campanhas Ativas"
                    value={currentOperation.activeCampaigns.toString()}
                    info="Total de campanhas ativas do dono da conta."
                    isLoading={isLoading.currentOperation}
                  />
                </div>
                <div className="col-md-4">
                  <DashboardMiniMetric
                    title="Tickets Vendidos"
                    value={currentOperation.soldTickets.toString()}
                    total={currentOperation.totalTickets.toString()}
                    info="Tickets pagos dentro das campanhas ativas sobre o total de tickets dessas campanhas."
                    isLoading={isLoading.currentOperation}
                  />
                </div>
                <div className="col-md-4">
                  <DashboardMiniMetric
                    title="Afiliados c/ Venda"
                    value={currentOperation.affiliatesWithSales.toString()}
                    total={currentOperation.totalAffiliates.toString()}
                    info="Afiliados que venderam ao menos um ticket pago nas campanhas ativas sobre o total de afiliados vinculados às campanhas ativas."
                    isLoading={isLoading.currentOperation}
                  />
                </div>
                <div className="col-12">
                  <div
                    className="bg-light rounded-3 p-3"
                    title="Percentual de tickets pagos em relação ao total de tickets das campanhas ativas."
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="fw-semibold">
                          Ocupação das Campanhas
                        </div>
                        <small className="text-muted">
                          Tickets pagos em relação ao total disponível nas
                          campanhas ativas
                        </small>
                      </div>
                      {isLoading.currentOperation ? (
                        <div style={{ width: "60px" }}>
                          <Skeleton count={1} />
                        </div>
                      ) : (
                        <div className="fs-5 fw-bold text-success">
                          {Number(
                            currentOperation.occupancyPercent || 0,
                          ).toFixed(2)}
                          %
                        </div>
                      )}
                    </div>
                    <div
                      className="progress rounded-pill"
                      style={{ height: "14px" }}
                    >
                      <div
                        className="progress-bar progress-bar-striped bg-success rounded-pill"
                        style={{
                          width: `${Math.min(
                            Number(currentOperation.occupancyPercent || 0),
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <small className="text-muted">0%</small>
                      <small className="text-muted">100%</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="card-title">Receita dos últimos 7 dias</div>
              <div className="card-category">
                Comissões recebidas e lucro por venda de tickets.
              </div>
            </div>
            <div className="card-body">
              <div
                className="d-flex align-items-end justify-content-between"
                style={{ minHeight: "220px" }}
              >
                {isLoading.weeklyRevenue ? (
                  <div className="w-100">
                    <Skeleton count={6} />
                  </div>
                ) : weeklyRevenue.days.length ? (
                  weeklyRevenue.days.map((item) => {
                    const height = maxWeeklyRevenue
                      ? Math.max((item.amount / maxWeeklyRevenue) * 100, 12)
                      : 12;

                    return (
                      <div
                        key={item.date}
                        className="text-center flex-fill px-1"
                      >
                        <div className="small text-muted mb-2">
                          {showValues
                            ? masks.brlCurrency(item.amount)
                            : "R$ ***"}
                        </div>
                        <div
                          className="d-flex align-items-end justify-content-center"
                          style={{ height: "150px" }}
                        >
                          <div
                            className="bg-primary rounded-top w-75"
                            title={`${item.label}: ${masks.brlCurrency(item.amount)} líquido / ${masks.brlCurrency(item.amountGross)} bruto`}
                            style={{ height: `${height}%` }}
                          />
                        </div>
                        <div className="fw-semibold small mt-2">
                          {item.label}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="w-100 text-center text-muted">
                    Nenhuma receita registrada nos últimos 7 dias.
                  </div>
                )}
              </div>
              {!isLoading.weeklyRevenue ? (
                <div
                  className="d-flex justify-content-between border-top mt-3 pt-3"
                  title="Total dos últimos 7 dias incluindo comissão recebida e lucro por venda de tickets."
                >
                  <span className="text-muted">Total líquido</span>
                  <strong>
                    {showValues
                      ? masks.brlCurrency(weeklyRevenue.totalAmount)
                      : "R$ *****"}
                  </strong>
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="card-title">Hoje</div>
              <div className="card-category">Movimentação do dia.</div>
            </div>
            <div className="card-body">
              <DashboardStat
                icon={<FaTicket />}
                title="Tickets vendidos"
                value={todayMovement.soldTickets.toString()}
                color="primary"
                isLoading={isLoading.todayMovement}
              />
              <DashboardStat
                icon={<FaBullseye />}
                title="Receita bruta"
                value={formatMoney(todayMovement.amountGross)}
                color="success"
                isLoading={isLoading.todayMovement}
              />
              <DashboardStat
                icon={<FaHandHoldingDollar />}
                title="Receita líquida"
                value={formatMoney(todayMovement.amount)}
                color="info"
                isLoading={isLoading.todayMovement}
              />
              <DashboardStat
                icon={<FaPercent />}
                title="Comissões geradas"
                value={formatMoney(todayMovement.generatedCommissions)}
                color="warning"
                isLoading={isLoading.todayMovement}
              />
            </div>
          </Card>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="card-title">Campanhas em destaque</div>
              <div className="card-category">
                Rifas com maior movimentação no período.
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Campanha</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Vendido</th>
                      <th className="text-center">Receita Bruta</th>
                      <th className="text-center">Afiliados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading.highlightCampaigns ? (
                      <tr>
                        <td colSpan={5}>
                          <Skeleton count={3} />
                        </td>
                      </tr>
                    ) : highlightCampaigns.campaigns.length ? (
                      highlightCampaigns.campaigns.map((campaign) => {
                        const status = getRaffleStatus(campaign.status);

                        return (
                          <tr key={campaign.key}>
                            <td className="fw-semibold" title={campaign.slug}>
                              <Link
                                className="text-primary"
                                to={`/raffle/default/view?campaign=${campaign.slug}&key=${campaign.key}`}
                                title="Abrir campanha"
                              >
                                {campaign.name}
                              </Link>
                            </td>
                            <td className="text-center">
                              <span
                                className={`badge badge-${status.color}`}
                                title={status.title}
                              >
                                {status.text}
                              </span>
                            </td>
                            <td
                              className="text-center"
                              title="Tickets pagos sobre o total de tickets da campanha."
                            >
                              {campaign.soldTickets} de {campaign.totalTickets}
                              <small className="text-muted ms-1">
                                ({Number(campaign.soldPercent || 0).toFixed(2)}
                                %)
                              </small>
                            </td>
                            <td
                              className="text-center"
                              title="Receita bruta das vendas pagas da campanha."
                            >
                              {formatMoney(campaign.amountGross)}
                            </td>
                            <td
                              className="text-center"
                              title="Afiliados distintos com venda paga nesta campanha."
                            >
                              {campaign.affiliatesWithSales}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          Nenhuma campanha ativa com movimentação.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="card-title">Ranking de afiliados</div>
              <div className="card-category">
                Top parceiros por vendas confirmadas.
              </div>
            </div>
            <div className="card-body">
              {isLoading.affiliateRanking ? (
                <Skeleton count={4} />
              ) : affiliateRanking.affiliates.length ? (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Afiliado</th>
                        <th>Tickets</th>
                        <th>Campanhas</th>
                        <th>Vendas</th>
                        <th>Comissão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliateRanking.affiliates.map((affiliate, index) => (
                        <tr
                          key={affiliate.id}
                          title="Ranking ordenado pelo maior volume bruto vendido."
                        >
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                style={{ width: "40px", height: "40px" }}
                              >
                                {index === 0 ? (
                                  <FaTrophy className="text-warning" />
                                ) : (
                                  <span className="fw-bold text-primary">
                                    {index + 1}
                                  </span>
                                )}
                              </div>
                              <div>
                                <Link
                                  className="fw-bold text-dark text-decoration-none d-block"
                                  to={`/profile?atsign=${affiliate.atSign}`}
                                  title="Abrir perfil do afiliado"
                                >
                                  {formatName(
                                    affiliate.name || affiliate.atSign,
                                  )}
                                </Link>
                                <Link
                                  className="badge badge-primary mt-1"
                                  to={`/profile?atsign=${affiliate.atSign}`}
                                  title="Abrir perfil do afiliado"
                                >
                                  <span
                                    className="text-white"
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    @{affiliate.atSign}
                                  </span>
                                </Link>
                              </div>
                            </div>
                          </td>
                          <td>
                            {affiliate.soldTickets}{" "}
                            {pluralize(
                              affiliate.soldTickets,
                              "ticket",
                              "tickets",
                            )}
                          </td>
                          <td>
                            {affiliate.campaignsWithSales}{" "}
                            {pluralize(
                              affiliate.campaignsWithSales,
                              "campanha",
                              "campanhas",
                            )}
                          </td>
                          <td>{formatMoney(affiliate.amountGross)}</td>
                          <td className="text-success">
                            {formatMoney(affiliate.commission)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  Nenhum afiliado com venda confirmada.
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* <div className="col-lg-5">
          <Card className="shadow-sm h-100">
            <div className="card-header">
              <div className="card-title">Atenção rápida</div>
              <div className="card-category">Itens para acompanhar antes de encerrar o dia.</div>
            </div>
            <div className="card-body">
              <div className="alert alert-warning d-flex align-items-center">
                <FaClock className="me-2" />
                <div>
                  <strong>{mockDashboard.owner.pendingWithdraws} saques</strong> aguardando conferência.
                </div>
              </div>
              <div className="alert alert-info d-flex align-items-center">
                <FaPeopleGroup className="me-2" />
                <div>
                  <strong>7 afiliados</strong> sem venda nos últimos 3 dias.
                </div>
              </div>
              <div className="alert alert-success d-flex align-items-center mb-0">
                <FaBullseye className="me-2" />
                <div>Campanha principal está acima da meta de conversão.</div>
              </div>
            </div>
          </Card>
        </div> */}
      </div>

      <ModalInfo
        header={modalInfo.header}
        title={modalInfo.title}
        text={modalInfo.text}
        isOpen={location?.hash === "#modalInfo"}
        onClose={() => {
          changeHash("");
        }}
      />

      {isWithdrawModalOpen && (
        <ModalPayoutRequest
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
          onCreated={getDashboardFinancialSummary}
        />
      )}
    </>
  );
}

interface IFinancialMetricProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  isLoading: boolean;
  onInfo: () => void;
}

function FinancialMetric({
  title,
  value,
  icon,
  isLoading,
  onInfo,
}: IFinancialMetricProps) {
  return (
    <div className="bg-light rounded-3 p-3 h-100">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="text-muted fw-semibold">{title}</div>
        <button type="button" className="btn p-0 text-muted" onClick={onInfo}>
          <FaCircleInfo />
        </button>
      </div>
      {isLoading ? (
        <Skeleton count={1} />
      ) : (
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="fw-bolder mb-0">{value}</h4>
          <div className="fs-3">{icon}</div>
        </div>
      )}
    </div>
  );
}

interface IDashboardMiniMetricProps {
  title: string;
  value: string;
  total?: string;
  info: string;
  isLoading?: boolean;
}

function DashboardMiniMetric({
  title,
  value,
  total,
  info,
  isLoading,
}: IDashboardMiniMetricProps) {
  return (
    <div
      className="bg-light rounded-3 p-3 h-100 border border-light"
      title={info}
    >
      <div className="text-muted small mb-2">{title}</div>
      {isLoading ? (
        <Skeleton count={1} />
      ) : (
        <div className="d-flex align-items-end">
          <div className="fs-3 fw-bold lh-1">{value}</div>
          {total ? (
            <div className="text-muted small ms-2">de {total}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

interface IDashboardStatProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  color: string;
  isLoading?: boolean;
}

function DashboardStat({
  icon,
  title,
  value,
  color,
  isLoading,
}: IDashboardStatProps) {
  return (
    <div
      className="d-flex justify-content-between align-items-center border-bottom py-3"
      title={title}
    >
      <div className="d-flex align-items-center">
        <div
          className={`bg-${color} text-white rounded-circle d-flex align-items-center justify-content-center me-3`}
          style={{ width: "42px", height: "42px" }}
        >
          {icon}
        </div>
        <span className="fw-semibold">{title}</span>
      </div>
      {isLoading ? (
        <div style={{ width: "70px" }}>
          <Skeleton count={1} />
        </div>
      ) : (
        <span className="fw-bold">{value}</span>
      )}
    </div>
  );
}

